import { Component } from '@angular/core';
import { Router } from '@angular/router';
import {
    DashboardClaim,
    ThirdPartyReferenceType
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    CreateAndInviteUserForCompanyRequest,
    CreateAndInviteUserForCompanyService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { getDynamicRouteLink } from '@lsq-frontend/common-ui';
import { getRouterLinkUrlById } from '@lsq-frontend/common-ui';
import { first } from 'rxjs/operators';
import { CompleteImplementationService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-complete-buyer-implementation/complete-implementation';
import {
    GetBuyerPrimaryContactRequest,
    GetBuyerPrimaryContactService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/get-buyer-primary-contact';
import { ADMIN_MANAGE_BUYER, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { ManageBuyerService } from '../manage-buyer.service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@Component({
    selector: 'admin-review-buyer',
    template: `
        <review-buyer-company-information></review-buyer-company-information>

        <review-buyer-company-location
            (completed)="setCompanyLocationIsComplete($event)"
        ></review-buyer-company-location>

        <review-buyer-configuration
            (completed)="setConfigurationIsComplete($event)"
        ></review-buyer-configuration>

        <review-buyer-company-contact
            (completed)="setContactIsComplete($event)"
        ></review-buyer-company-contact>

        <div class="row">
            <div class="col-12 col-md-3" [style.marginTop]="'1rem'">
                <lsq-button
                    appearance="primary"
                    size="block"
                    [disabled]="!isComplete()"
                    [submitting]="isSubmitting"
                    (buttonClick)="completeImplementation()"
                >
                    Complete Implementation
                </lsq-button>
            </div>
        </div>
    `
})
export class AdminReviewBuyerComponent {
    public companyLocationIsComplete = false;
    public configurationIsComplete = false;
    public contactIsComplete = false;

    public isSubmitting = false;

    constructor(
        private _completeImplementationService: CompleteImplementationService,
        private _manageBuyerService: ManageBuyerService,
        private _alertService: AlertService,
        private _router: Router,
        private _getBuyerPrimaryContactService: GetBuyerPrimaryContactService,
        private _createAndInviteUserForCompanyService: CreateAndInviteUserForCompanyService
    ) {}

    public isComplete(): boolean {
        return (
            this.companyLocationIsComplete && this.configurationIsComplete && this.contactIsComplete
        );
    }

    public setCompanyLocationIsComplete(isComplete: boolean): void {
        this.companyLocationIsComplete = isComplete;
    }
    public setConfigurationIsComplete(isComplete: boolean): void {
        this.configurationIsComplete = isComplete;
    }
    public setContactIsComplete(isComplete: boolean): void {
        this.contactIsComplete = isComplete;
    }

    public async completeImplementation(): Promise<void> {
        this.isSubmitting = true;

        try {
            const response = await this._completeImplementationService.executeOnce({
                buyerImplementationKey: await this._manageBuyerService.buyerKey$
                    .pipe(first())
                    .toPromise()
            });

            await this.createUserForClient(response.buyerKey, response.companyKey);
            const manageBuyerUrl = getRouterLinkUrlById(atlas, ADMIN_MANAGE_BUYER);
            const routerLink = getDynamicRouteLink(manageBuyerUrl, {
                buyerKey: response.buyerKey
            });

            this._router.navigateByUrl(routerLink);
        } catch {
            this._alertService.error('Error creating buyer. Please try again.');
        }

        this.isSubmitting = false;
    }

    public async createUserForClient(buyerKey: string, companyKey: string): Promise<void> {
        const primaryContactResponse = await this._getBuyerPrimaryContactService.executeOnce(
            new GetBuyerPrimaryContactRequest({
                buyerKey: buyerKey
            })
        );

        const createAndInviteUserForCompanyRequest = new CreateAndInviteUserForCompanyRequest({
            companyKey,
            thirdPartyKey: buyerKey,
            thirdPartyReferenceType: ThirdPartyReferenceType.FinancingBuyer,
            firstName: primaryContactResponse.primaryContactFirstName,
            lastName: primaryContactResponse.primaryContactLastName,
            emailAddress: primaryContactResponse.primaryContactEmail,
            phoneNumber: '1111111111',
            title: 'N/A',
            claimsToAdd: [
                DashboardClaim.FinancingBuyerPortal,
                DashboardClaim.FinancingBuyerPortalEditInvoices,
                DashboardClaim.FinancingBuyerPortalEditUsers,
                DashboardClaim.FinancingBuyerPortalViewInvoices,
                DashboardClaim.FinancingBuyerPortalViewUsers
            ]
        });

        await this._createAndInviteUserForCompanyService.executeOnce(
            createAndInviteUserForCompanyRequest
        );
    }
}
